import React from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'

import Form from 'components/Form'
import { Text } from 'components/Box'
import IndexLayout, { Title } from 'components/IndexLayout'
import PasswordInput from 'components/PasswordInput'
import Button from 'components/Button'
import { useToast } from 'containers/ToastManager'

import { RouteProps, routes } from 'constants/routes'

import { getResetToken, clearResetToken } from 'utils'
import { useUserPasswordResetMutation } from 'gqlTypes'
import { Location } from 'history'

const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .min(8, 'Password is too short (minimum is 8 characters)')
    .max(72, 'Password is too long (maximum is 72 characters)')
    .required('Password can’t be blank'),
})

const INITIAL_VALUES = {
  password: '',
}

interface LocationState {
  from: string
}

interface ResetPasswordViewProps extends RouteProps<'RESET_PASSWORD'> {
  location: Location<LocationState>
}

const ResetPasswordView: React.FC<ResetPasswordViewProps> = props => {
  const { location, history } = props
  const { showToast } = useToast()

  const [resetPassword, { loading, data }] = useUserPasswordResetMutation({
    redirectErrors: false,
    onError: error => {
      showToast(error.message, 'negative')
    },
    onCompleted: data => {
      const { successful } = data.userPasswordReset || {}

      if (successful) {
        showToast('Your password has been reset! Please sign in.')
        history.push(routes.SIGN_IN.create({}))

        // Clear the token from local-storage
        clearResetToken()
      }
    },
  })

  const apiErrors = data?.userPasswordReset?.errors || []

  const handleSubmit = (values: typeof INITIAL_VALUES) => {
    /**
     * Check for reset token presence and
     * handle it to prevent referrer leak
     * https://github.com/sketch-hq/Cloud/issues/3898
     */
    const token = getResetToken(location)

    if (!token) {
      showToast(
        'Something is wrong, please contact our support team',
        'negative'
      )
      return
    }

    resetPassword({
      variables: {
        newPassword: values.password,
        token,
      },
    })
  }

  return (
    <IndexLayout headerLink="sign-in" variant="teal">
      <Title>Enter Your New Password</Title>

      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={resetPasswordSchema}
        onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            <Form.Field
              name="password"
              label="New Password"
              errorText={touched.password ? errors.password : undefined}>
              <PasswordInput
                name="password"
                strengthMeter
                value={values.password}
                autoFocus
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </Form.Field>

            {apiErrors.length > 0 && (
              <Form.Error>
                <Text textAlign="center">
                  {apiErrors.map(error => error?.message).join('; ')}
                </Text>
              </Form.Error>
            )}

            <Form.Footer>
              <Button
                variant="primary-untinted"
                fill
                type="submit"
                loading={loading}>
                Change Password
              </Button>
            </Form.Footer>
          </Form>
        )}
      </Formik>
    </IndexLayout>
  )
}

export default ResetPasswordView
